<template>
    <div>
        <div class="flex">
            <h2 class="leading-snug flex-grow text-heading-2 font-semibold mb-32 first-letter:uppercase">
                <label-with-missing-translation :label="competence.title[locale]"/>
            </h2>
            <div class="flex flex-row-reverse">
                <tooltip
                    v-for="(user, i) in users"
                    :key="user.id"
                    :content="`${user.name}: ${user.score}%`"
                    class="w-32 h-32"
                    position="bottom"
                >
                    <avatar
                        :image="user.image_url"
                        size="small"
                        class="cursor-default border border-white"
                    >
                        {{ user.initials }}
                    </avatar>
                </tooltip>
            </div>
        </div>
        <div class="flex">
            <div class="flex-grow flex items-center justify-between -my-16">
                <score :score="myScore ? myScore.score : null" class="w-48 h-48"
                       :class="!myScore ? 'opacity-30' : ''"></score>
                <range-slider
                    :value="myScore ? myScore.score : 0"
                    :min="0"
                    :max="100"
                    :step="5"
                    :change-color="true"
                    @input="toggleScore"
                    class="mx-32"
                />
                <strong class="block w-80">
                    {{ myScore ? myScore.score + '%' : '-' }}
                </strong>

                <button
                    v-if="myScore"
                    class="flex text-primary-80"
                    @click="toggleScore(null)"
                >
                    <svg-icon class="max-w-14 max-h-14 opacity-75"
                              type="replay"/>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import _debounce from 'lodash/debounce';
import Tooltip from './navigation/Tooltip.vue';
import Avatar from './ui/Avatar.vue';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';
import ApplicationCompetence from '../models/ApplicationCompetence';
import Application from '../models/Application';
import * as competences from '../services/CompetenceService';
import {normalizeJson} from '../../shared/helpers';
import JobCompetence from '../models/JobCompetence';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import * as applications from '../services/ApplicationService';
import RangeSlider from "./ui/RangeSlider.vue";
import Score from "./ui/Score.vue";
import SvgIcon from './ui/SvgIcon';
import LabelWithMissingTranslation from "./LabelWithMissingTranslation.vue";

export default {
    components: {
        Score,
        SvgIcon,
        'range-slider': RangeSlider,
        'tooltip': Tooltip,
        'avatar': Avatar,
        'popup-modal-confirm': PopupModalConfirm,
        LabelWithMissingTranslation
    },
    props: {
        competence: {
            required: true,
            type: Object,
        },
        application: {
            required: true,
            type: Application,
        }
    },
    data: function () {
        return {
            score: 0,
            savingScore: false,
            deleting: false,
            performingDeleteRequest: false,
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useAuthenticationStore, ['currentUserId']),
        can () {
            return {
                jobs: { update: this.$can('jobs.update') || this.$can('applications.update') },
            };
        },
        applicationCompetences () {
            return useRepo(ApplicationCompetence)
                .with('user')
                .where('application_id', this.application.id)
                .where('competence_id', this.competence.id)
                .get();
        },
        myScore() {
            return this.applicationCompetences.find(entry => entry.user_id === this.currentUserId) || null;
        },
        getScore() {
            return this.applicationCompetences.filter(entry => entry.user_id !== this.currentUserId);
        },
        users() {
            return this.applicationCompetences.map((entry) => {
                entry.user.score = entry.score;
                return entry.user;
            });
        },
    },
    methods: {
        toggleScore: _debounce(function (score) {
            // If a request to save the score for this competence is still busy, we don't
            // want to trigger another one to prevent race conditions.
            if (this.savingScore) {
                return;
            }

            this.savingScore = true;
            let action;

            if (score === null || (this.myScore && this.myScore.score === score)) {
                action = this.deleteScore();
            } else if (this.myScore) {
                action = this.updateScore(score);
            } else {
                action = this.createScore(score);
            }

            action.then(application => {
                useRepo(Application).save(application);
            }).then(updates => {
                this.savingScore = false;
            });
        }, 500),
        deleteScore() {
            let originalScore = {...this.myScore};

            useRepo(ApplicationCompetence).destroy(this.myScore.id);

            return competences.deleteCompetenceScore(this.application.id, {
                data: {id: originalScore.id}
            }).catch(error => {
                useRepo(ApplicationCompetence).save(originalScore);
            }).then(result => {
                return normalizeJson(result);
            });
        },
        updateScore (score) {
            const doUpdateScore = (id, score) => {
                useRepo(ApplicationCompetence)
                    .whereId(id)
                    .update({score: this.toInt(score)});
            }

            let originalScore = this.myScore.score;
            doUpdateScore(this.myScore.id, score)

            return competences.updateCompetenceScore(this.application.id, {
                data: {
                    id: this.competence.id,
                    user_id: this.currentUserId,
                    pivot: { score }
                }
            }).catch(error => {
                doUpdateScore(this.myScore.id, originalScore);
            }).then(result => {
                return normalizeJson(result).application;
            });
        },
        createScore(score) {
            let insertedCompetence = useRepo(ApplicationCompetence).save({
                application_id: this.application.id,
                competence_id: this.competence.id,
                user_id: this.currentUserId,
                score: this.toInt(score),
            });

            const deleteThis = (insertedCompetence) => {
                useRepo(ApplicationCompetence)
                    .where('application_id', insertedCompetence.application_id)
                    .where('competence_id', insertedCompetence.competence_id)
                    .where('user_id', insertedCompetence.user_id)
                    .where('score', insertedCompetence.score)
                    .delete();
            };

            return competences.storeCompetenceScore(this.application.id, {
                data: {
                    id: this.competence.id,
                    user_id: this.currentUserId,
                    pivot: { score }
                }
            }).catch(error => {
                deleteThis(insertedCompetence);
            }).then(result => {
                deleteThis(insertedCompetence);

                let data = normalizeJson(result);
                data.score = this.toInt(data.score);
                useRepo(ApplicationCompetence).save(data);

                return data.application;
            });
        },
        confirmDelete () {
            this.deleting = true;
        },
        cancelDelete () {
            this.deleting = false;
        },
        deleteCompetence () {
            this.performingDeleteRequest = true;

            competences.destroyForJob(this.application.stage.job_id, {
                data: { id: this.competence.id }
            }).then(() => {
                useRepo(JobCompetence)
                    .where('competence_id', this.competence.id)
                    .where('job_id', this.application.stage.job_id)
                    .delete();
            }).then(() => {
                return applications.find(this.application.id);
            }).then(result => {
                useRepo(Application).save(normalizeJson(result));
            });
        },
        toInt(value) {
            return parseInt(value, 10) || 0;
        }
    },
}
</script>
